import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { gsap } from 'gsap';
import { CustomEase } from 'gsap/CustomEase';

import { useBusinessSolutionsData } from './hooks';
import SolutionsPanels from './SolutionsPanels';
import SolutionsTabs from './SolutionsTabs';
import SolutionsBackgrounds from './SolutionsBackgrounds';
import { BUSINESS_SOLUTIONS_VARIABLES } from './constants';

import { RichText } from 'components/Prismic';
import { Normalize } from 'utils/types';

export type TBusinessSolutionsSection = Normalize<Queries.HomeContentBusinessSolutionsDataFragment>;

const ease = CustomEase.create(
  'custom',
  'M0,0 C0.264,0 0.275,0.057 0.3,0.1 0.344,0.176 0.368,0.29 0.4,0.5 0.434,0.728 0.53,0.811 0.59,0.86 0.636,0.898 0.818,0.986 1,1 '
);

const Container = styled.div`
  ${BUSINESS_SOLUTIONS_VARIABLES.backgroundColor}: #e6e6e6;
  ${BUSINESS_SOLUTIONS_VARIABLES.foregroundColor}: #ffffff80;
  ${BUSINESS_SOLUTIONS_VARIABLES.primaryTextColor}: #000;
  ${BUSINESS_SOLUTIONS_VARIABLES.secondaryTextColor}: #464646;
  ${BUSINESS_SOLUTIONS_VARIABLES.tertiaryTextColor}: #0009;
  ${BUSINESS_SOLUTIONS_VARIABLES.mobileSelectColor}: #292929;

  display: grid;
  position: relative;
  grid-template-columns: 1fr;
  background-color: var(--background-color);
  overflow: hidden;
`;

const Content = styled.div`
  ${({ theme: { mq, spacings } }) => css`
    display: grid;
    grid-row: 1 / span 1;
    grid-column: 1 / -1;
    grid-auto-rows: min-content;
    z-index: 1;
    padding: 17.25rem 33.375rem;

    ${mq.md} {
      padding: 6.75rem ${spacings.padding} 3rem;
    }
  `}
`;

const Title = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;

    & > h2 {
      text-align: center;
      line-height: 1.2;
      color: var(--secondary-text-color);
      font-size: 8rem;

      ${tp.useFont(fonts => fonts.Switzer.regular)};
    }

    ${mq.md} {
      & > h2 {
        font-size: 3.75rem;
      }
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { mq, tp } }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;

    & > h4 {
      padding: 2.75rem 0 6rem;
      text-align: center;
      line-height: 1.5;
      color: var(--tertiary-text-color);
      font-size: 2.75rem;

      ${tp.useFont(fonts => fonts.Switzer.regular)};
    }
    ${mq.md} {
      display: none;
    }
  `}
`;

const BusinessSolutionsSection: React.FC<TBusinessSolutionsSection> = sliceData => {
  const { title, subtitle, solutions } = useBusinessSolutionsData(sliceData);

  const containerRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [isReverse, setIsReverse] = useState(false);

  const handleChange = useCallback((index: number) => {
    setActiveIndex(index);
    setIsReverse(reverse => !reverse);
  }, []);

  useLayoutEffect(() => {
    if (activeIndex !== null && containerRef.current) {
      const { isDark } = solutions[activeIndex];

      gsap.to(containerRef.current, {
        [BUSINESS_SOLUTIONS_VARIABLES.backgroundColor]: isDark ? '#626262' : '#e6e6e6',
        [BUSINESS_SOLUTIONS_VARIABLES.foregroundColor]: isDark ? '#00000080' : '#ffffff80',
        [BUSINESS_SOLUTIONS_VARIABLES.primaryTextColor]: isDark ? '#ffffff' : '#000000',
        [BUSINESS_SOLUTIONS_VARIABLES.secondaryTextColor]: isDark ? '#ffffff80' : '#464646',
        [BUSINESS_SOLUTIONS_VARIABLES.tertiaryTextColor]: isDark ? '#ffffff99' : '#00000099',
        [BUSINESS_SOLUTIONS_VARIABLES.mobileSelectColor]: isDark ? '#292929' : '#ffffff',

        ease,
        duration: 1,
      });
    }
  }, [activeIndex]);

  return (
    <Container ref={containerRef}>
      <SolutionsBackgrounds
        solutions={solutions}
        activeIndex={activeIndex}
        isReverse={isReverse}
        containerRef={containerRef}
      />
      <Content>
        <Title render={title} />
        <Description render={subtitle} />
        <SolutionsTabs
          solutions={solutions}
          activeIndex={activeIndex}
          handleChange={handleChange}
        />
        <SolutionsPanels solutions={solutions} activeIndex={activeIndex} />
      </Content>
    </Container>
  );
};

export const contentBusinessSolutionsFragment = graphql`
  fragment HomeContentBusinessSolutionsData on PrismicHomeDataContentHomebusinesssolutions {
    slice_type
    primary {
      home_business_solutions_subtitle {
        richText
      }
      home_business_solutions_title {
        richText
      }
    }
    items {
      home_business_solutions_is_dark
      ...HomeContentBusinessSolutionsSolutionsBackgroundsData
      ...HomeContentBusinessSolutionsSolutionsTabsData
      ...HomeContentBusinessSolutionsSolutionsPanelsData
    }
  }
`;

export default BusinessSolutionsSection;
